export type Engine = "postgres" | "mysql" | "oracle" | "mssql" | "mongo" | "db2";

export type Mode = "create" | "update";

export type Preset = "readonly" | "readwrite" | "app_owner" | "migrations" | "custom";

export interface BuilderState {
  engine: Engine;
  mode: Mode;

  dbName: string;
  encoding: string;
  locale: string;
  template: string;

  userName: string;
  password: string;
  validUntil: string; // ISO date or ""
  connectionLimit: number; // -1 unlimited
  hostPattern: string; // mysql user@host

  // role attributes
  canLogin: boolean;
  superuser: boolean;
  createDb: boolean;
  createRole: boolean;
  replication: boolean;
  bypassRls: boolean;
  inheritRole: boolean;

  // schema / isolation
  ownDatabase: boolean; // user owns the database
  createSchema: boolean;
  schemaName: string;
  setSearchPath: boolean;
  revokePublicSchema: boolean; // revoke from PUBLIC on public schema in this db
  revokeOtherDbConnect: boolean; // REVOKE CONNECT ON DATABASE ... FROM PUBLIC for created db
  hideOtherDatabases: boolean; // note-only / pg_hba comment

  // privileges
  preset: Preset;
  privSelect: boolean;
  privInsert: boolean;
  privUpdate: boolean;
  privDelete: boolean;
  privTruncate: boolean;
  privReferences: boolean;
  privTrigger: boolean;
  privUsageSeq: boolean;
  privSelectSeq: boolean;
  privUpdateSeq: boolean;
  privExecuteFn: boolean;
  privUsageSchema: boolean;
  privCreateSchema: boolean;
  defaultPrivileges: boolean; // apply ALTER DEFAULT PRIVILEGES for future objects
  defaultPrivilegesFor: string; // role that creates objects, defaults to owner/current user

  // update mode
  resetPassword: boolean;
  revokeBeforeGrant: boolean;
  dropOwnedReassign: boolean;

  // connection string
  host: string;
  port: number;

  // misc
  wrapTransaction: boolean;
  includeComments: boolean;
  quoteIdentifiers: boolean;
}

export const defaultState: BuilderState = {
  engine: "postgres",
  mode: "create",

  dbName: "app_db",
  encoding: "UTF8",
  locale: "en_US.UTF-8",
  template: "template0",

  userName: "app_user",
  password: "",
  validUntil: "",
  connectionLimit: -1,
  hostPattern: "%",

  canLogin: true,
  superuser: false,
  createDb: false,
  createRole: false,
  replication: false,
  bypassRls: false,
  inheritRole: true,

  ownDatabase: true,
  createSchema: true,
  schemaName: "app",
  setSearchPath: true,
  revokePublicSchema: true,
  revokeOtherDbConnect: true,
  hideOtherDatabases: true,

  preset: "app_owner",
  privSelect: true,
  privInsert: true,
  privUpdate: true,
  privDelete: true,
  privTruncate: false,
  privReferences: true,
  privTrigger: false,
  privUsageSeq: true,
  privSelectSeq: true,
  privUpdateSeq: true,
  privExecuteFn: true,
  privUsageSchema: true,
  privCreateSchema: false,
  defaultPrivileges: true,
  defaultPrivilegesFor: "",

  resetPassword: true,
  revokeBeforeGrant: true,
  dropOwnedReassign: false,

  host: "localhost",
  port: 5432,

  wrapTransaction: true,
  includeComments: true,
  quoteIdentifiers: true,
};

export const presets: Record<Exclude<Preset, "custom">, Partial<BuilderState>> = {
  readonly: {
    privSelect: true,
    privInsert: false,
    privUpdate: false,
    privDelete: false,
    privTruncate: false,
    privReferences: false,
    privTrigger: false,
    privUsageSeq: true,
    privSelectSeq: true,
    privUpdateSeq: false,
    privExecuteFn: true,
    privUsageSchema: true,
    privCreateSchema: false,
    defaultPrivileges: true,
  },
  readwrite: {
    privSelect: true,
    privInsert: true,
    privUpdate: true,
    privDelete: true,
    privTruncate: false,
    privReferences: true,
    privTrigger: false,
    privUsageSeq: true,
    privSelectSeq: true,
    privUpdateSeq: true,
    privExecuteFn: true,
    privUsageSchema: true,
    privCreateSchema: false,
    defaultPrivileges: true,
  },
  app_owner: {
    privSelect: true,
    privInsert: true,
    privUpdate: true,
    privDelete: true,
    privTruncate: true,
    privReferences: true,
    privTrigger: true,
    privUsageSeq: true,
    privSelectSeq: true,
    privUpdateSeq: true,
    privExecuteFn: true,
    privUsageSchema: true,
    privCreateSchema: true,
    defaultPrivileges: true,
  },
  migrations: {
    privSelect: true,
    privInsert: true,
    privUpdate: true,
    privDelete: true,
    privTruncate: true,
    privReferences: true,
    privTrigger: true,
    privUsageSeq: true,
    privSelectSeq: true,
    privUpdateSeq: true,
    privExecuteFn: true,
    privUsageSchema: true,
    privCreateSchema: true,
    createDb: false,
    createRole: false,
    defaultPrivileges: true,
  },
};
